import React, { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  CForm,
  CFormLabel,
  CFormInput,
  CButton,
  CAlert,
} from "@coreui/react"
import { Eye, EyeOff, Check, ShieldCheck } from "lucide-react"
import "./LoginPage.css"
import { useAuth } from "../hooks/useAuth"
import {
  registerUser,
  verifyEmail,
  resendEmailVerification,
  verifyPhone,
  resendPhoneVerification,
  loginUser,
  verifyLoginOtp,
  resendLoginOtp,
} from "../api/auth"

const RESEND_COOLDOWN_SECONDS = 30

// ---------------------------------------------------------------
// Registration milestones.
// "Start" is already complete when the user lands on the form, so the
// bar never begins empty.
// ---------------------------------------------------------------
const REG_MILESTONES = [
  { key: "start", label: "Start" },
  { key: "details", label: "Details" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "done", label: "Done" },
]
const REG_STEP_TITLES = {
  details: "Your details",
  email: "Verify email",
  phone: "Verify phone",
  done: "Complete",
}
const ACTIONABLE_STEPS = 3 // details, email, phone

const RegProgress = ({ step }) => {
  const total = REG_MILESTONES.length
  const current = REG_MILESTONES.findIndex((m) => m.key === step)
  const isDone = step === "done"
  const percent = Math.round((current / (total - 1)) * 100)

  return (
    <div className="reg-progress" style={{ "--steps": total }}>
      <div className="reg-progress-head">
        <span className="reg-progress-now">
          {isDone ? "All set" : `Step ${current} of ${ACTIONABLE_STEPS}`}
          <strong>{REG_STEP_TITLES[step]}</strong>
        </span>
        <span className="reg-progress-pct">{percent}%</span>
      </div>

      <div
        className="reg-stepper"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-valuetext={`${REG_STEP_TITLES[step]}, ${percent}% complete`}
      >
        <div className="reg-stepper-line">
          <div className="reg-stepper-fill" style={{ width: `${percent}%` }} />
        </div>

        <ol className="reg-stepper-list">
          {REG_MILESTONES.map((m, i) => {
            const state =
              isDone || i < current
                ? "complete"
                : i === current
                  ? "current"
                  : "upcoming"

            return (
              <li
                key={m.key}
                className={`reg-step is-${state}`}
                aria-current={state === "current" ? "step" : undefined}
              >
                <span className="reg-step-dot">
                  {state === "complete" && <Check size={12} strokeWidth={3.5} />}
                </span>
                <span className="reg-step-label">{m.label}</span>
              </li>
            )
          })}
        </ol>
      </div>
    </div>
  )
}

// Turns whatever the backend sends back into a readable message.
const getErrorMessage = (err, fallback) => {
  const detail = err.response?.data?.detail

  if (Array.isArray(detail)) {
    return detail.map((d) => d.msg).filter(Boolean).join(", ") || fallback
  }
  if (typeof detail === "string") return detail
  if (detail && typeof detail === "object") {
    let msg = detail.message || fallback
    if (typeof detail.attempts_remaining === "number") {
      msg += ` (${detail.attempts_remaining} attempt${
        detail.attempts_remaining === 1 ? "" : "s"
      } remaining)`
    }
    return msg
  }
  return fallback
}

const normalizePhone = (value) => value.replace(/[\s()-]/g, "")
const isValidPhone = (value) => /^\+\d{8,15}$/.test(normalizePhone(value))

const CodeField = ({ id, value, onChange }) => (
  <>
    <CFormLabel htmlFor={id} className="login-label">
      Verification code
    </CFormLabel>
    <CFormInput
      id={id}
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      className="mb-3 login-code-input"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
      placeholder="••••••"
      autoFocus
      required
    />
  </>
)

const LoginPage = () => {
  const [mode, setMode] = useState("login") // "login" | "register"

  // ---------------- Login state ----------------
  const [loginStep, setLoginStep] = useState("credentials") // "credentials" | "phone"
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [challengeId, setChallengeId] = useState("")
  const [loginCode, setLoginCode] = useState("")
  const [trustDevice, setTrustDevice] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  // ---------------- Registration state ----------------
  const [regStep, setRegStep] = useState("details") // "details" | "email" | "phone" | "done"
  const [regUsername, setRegUsername] = useState("")
  const [regEmail, setRegEmail] = useState("")
  const [regPhone, setRegPhone] = useState("")
  const [regPassword, setRegPassword] = useState("")
  const [regConfirm, setRegConfirm] = useState("")
  const [showRegPassword, setShowRegPassword] = useState(false)
  const [showRegConfirm, setShowRegConfirm] = useState(false)
  const [emailCode, setEmailCode] = useState("")
  const [phoneCode, setPhoneCode] = useState("")

  // ---------------- General state ----------------
  const [error, setError] = useState("")
  const [info, setInfo] = useState("")
  const [submitting, setSubmitting] = useState(false)

  // refreshUser is optional: if your AuthContext doesn't expose it,
  // we fall back to a full reload so the context re-reads /auth/me.
  const { refreshUser } = useAuth()

  const navigate = useNavigate()
  const location = useLocation()
  const from = location.state?.from?.pathname || "/"

  // Resend cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000)
    return () => clearTimeout(t)
  }, [resendCooldown])

  const resetMessages = () => {
    setError("")
    setInfo("")
  }

  const switchMode = (next) => {
    setMode(next)
    resetMessages()
    setLoginStep("credentials")
    setLoginCode("")
    setTrustDevice(false)
    setResendCooldown(0)
    setRegStep("details")
    setEmailCode("")
    setPhoneCode("")
  }

  // ==================================================
  // LOGIN
  // ==================================================

  // Shared by both login paths (trusted device, or after the phone code)
  const completeLogin = async () => {
    if (typeof refreshUser === "function") {
      await refreshUser()
      navigate(from, { replace: true })
    } else {
      window.location.replace(from)
    }
  }

  // Step 1: credentials -> either logged in (trusted device)
  // or the backend sends a code to the user's phone
  const handleLogin = async (e) => {
    e.preventDefault()
    resetMessages()
    setSubmitting(true)

    try {
      const { data } = await loginUser(username, password)

      // Trusted browser: backend already set the session cookie, skip the code
      if (data.trusted_device) {
        await completeLogin()
        return
      }

      setChallengeId(data.challenge_id)
      setLoginCode("")
      setTrustDevice(false)
      setResendCooldown(RESEND_COOLDOWN_SECONDS)
      setLoginStep("phone")
      setInfo("We sent a verification code to your phone.")
    } catch (err) {
      setError(getErrorMessage(err, "Invalid username or password."))
    } finally {
      setSubmitting(false)
    }
  }

  // Step 2: phone code (+ optional trust device) -> login complete
  const handleLoginCode = async (e) => {
    e.preventDefault()
    resetMessages()

    if (loginCode.length !== 6) {
      setError("Please enter the 6-digit verification code.")
      return
    }

    setSubmitting(true)

    try {
      await verifyLoginOtp(challengeId, loginCode, trustDevice)
      await completeLogin()
    } catch (err) {
      setError(getErrorMessage(err, "Invalid verification code."))
    } finally {
      setSubmitting(false)
    }
  }

  const handleResendLoginCode = async () => {
    resetMessages()
    setSubmitting(true)

    try {
      await resendLoginOtp(challengeId)
      setLoginCode("")
      setResendCooldown(RESEND_COOLDOWN_SECONDS)
      setInfo("A new code has been sent to your phone.")
    } catch (err) {
      // Backend may say "wait a few seconds" - respect it
      if (err.response?.status === 429) {
        setResendCooldown(RESEND_COOLDOWN_SECONDS)
      }
      setError(getErrorMessage(err, "Could not resend the verification code."))
    } finally {
      setSubmitting(false)
    }
  }

  const handleBackToLogin = () => {
    resetMessages()
    setLoginCode("")
    setChallengeId("")
    setPassword("")
    setTrustDevice(false)
    setResendCooldown(0)
    setLoginStep("credentials")
  }

  // ==================================================
  // REGISTRATION
  // ==================================================

  // Step 1: details -> backend sends email + phone codes
  const handleRegister = async (e) => {
    e.preventDefault()
    resetMessages()

    if (regPassword !== regConfirm) {
      setError("Passwords do not match.")
      return
    }

    if (!isValidPhone(regPhone)) {
      setError(
        "Enter your phone number with the country code, e.g. +201001234567."
      )
      return
    }

    setSubmitting(true)

    try {
      await registerUser(
        regUsername,
        regEmail,
        normalizePhone(regPhone),
        regPassword
      )
      setEmailCode("")
      setRegStep("email")
      setInfo(`We sent a verification code to ${regEmail}.`)
    } catch (err) {
      setError(getErrorMessage(err, "Could not start registration."))
    } finally {
      setSubmitting(false)
    }
  }

  // Both factors verified -> show the success panel (progress hits 100%)
  const finishRegistration = () => {
    resetMessages()
    setRegStep("done")
  }

  // From the success panel back to the sign-in form
  const goToSignIn = () => {
    setUsername(regUsername)

    setRegUsername("")
    setRegEmail("")
    setRegPhone("")
    setRegPassword("")
    setRegConfirm("")
    setEmailCode("")
    setPhoneCode("")
    setRegStep("details")
    setLoginStep("credentials")
    setMode("login")
    setError("")
    setInfo("Your account is awaiting administrator approval.")
  }

  // The verify endpoints return either the created user (everything verified)
  // or a status object (something is still pending).
  const isRegistrationComplete = (data) =>
    Boolean(data && (data.id || data.username))

  // Step 2: email code
  const handleVerifyEmail = async (e) => {
    e.preventDefault()
    resetMessages()

    if (emailCode.length !== 6) {
      setError("Please enter the 6-digit verification code.")
      return
    }

    setSubmitting(true)

    try {
        const { data } = await verifyEmail(regEmail, emailCode)

        if (isRegistrationComplete(data)) {
          finishRegistration()
          return
        }

        // The backend sends the phone code after email verification.
        setPhoneCode("")
        setRegStep("phone")
        setInfo(
          `Email verified. We sent a verification code to ${normalizePhone(regPhone)}.`
        )
      } catch (err) {
        setError(getErrorMessage(err, "Invalid email verification code."))
      } finally {
        setSubmitting(false)
      }
    }

  // Step 3: phone code
  const handleVerifyPhone = async (e) => {
    e.preventDefault()
    resetMessages()

    if (phoneCode.length !== 6) {
      setError("Please enter the 6-digit verification code.")
      return
    }

    setSubmitting(true)

    try {
      const { data } = await verifyPhone(regEmail, phoneCode)

      if (isRegistrationComplete(data)) {
        finishRegistration()
      } else if (data?.email_verified === false) {
        // Phone verified but email still pending
        setRegStep("email")
        setInfo("Phone verified. Please verify your email to finish.")
      } else {
        finishRegistration()
      }
    } catch (err) {
      setError(getErrorMessage(err, "Invalid verification code."))
    } finally {
      setSubmitting(false)
    }
  }

  const handleResend = async (type) => {
    resetMessages()
    setSubmitting(true)

    try {
      if (type === "email") {
        await resendEmailVerification(regEmail)
        setEmailCode("")
        setInfo("A new code has been sent to your email.")
      } else {
        await resendPhoneVerification(regEmail)
        setPhoneCode("")
        setInfo("A new code has been sent to your phone.")
      }
    } catch (err) {
      setError(getErrorMessage(err, "Could not resend the verification code."))
    } finally {
      setSubmitting(false)
    }
  }

  const handleBackToDetails = () => {
    resetMessages()
    setEmailCode("")
    setPhoneCode("")
    setRegStep("details")
  }

  // ==================================================
  // RENDER HELPERS
  // ==================================================

  const passwordField = (id, label, value, setValue, show, setShow) => (
    <>
      <CFormLabel htmlFor={id} className="login-label">
        {label}
      </CFormLabel>
      <div className="password-input-wrapper">
        <CFormInput
          id={id}
          type={show ? "text" : "password"}
          className="password-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          required
        />
        <button
          type="button"
          className="password-toggle"
          onClick={() => setShow((prev) => !prev)}
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </>
  )

  const title =
    mode === "login"
      ? loginStep === "phone"
        ? "Two-step verification"
        : "Sign in"
      : {
          details: "Create your account",
          email: "Verify your email",
          phone: "Verify your phone",
          done: "You're all set",
        }[regStep]

  const subtitle =
    mode === "login"
      ? loginStep === "phone"
        ? "Enter the code we sent to your phone"
        : "Welcome back!"
      : {
          details: "Tell us a bit about yourself",
          email: "Check your inbox for the verification code",
          phone: "Check your phone for the verification code",
          done: "Registration complete",
        }[regStep]

  // Changing this key re-mounts the wrapper and replays the entrance animation
  const stepKey = mode === "login" ? `login-${loginStep}` : `reg-${regStep}`

  // ==================================================
  // RENDER
  // ==================================================

  return (
    <div className="login-page">
      <div className={`login-card${mode === "register" ? " is-register" : ""}`}>
        <div className="login-brand">
          <img src="/logo.png" alt="logo" className="login-brand-icon" />
        </div>

        <h3 className="login-title">{title}</h3>
        <p className="login-subtitle">{subtitle}</p>

        {mode === "register" && <RegProgress step={regStep} />}

        {error && <CAlert color="danger">{error}</CAlert>}
        {info && <CAlert color="success">{info}</CAlert>}

        <div key={stepKey} className="login-step">
          {/* ================= LOGIN: credentials ================= */}
          {mode === "login" && loginStep === "credentials" && (
            <CForm onSubmit={handleLogin}>
              <CFormLabel htmlFor="username" className="login-label">
                Username
              </CFormLabel>
              <CFormInput
                id="username"
                className="mb-3"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />

              {passwordField(
                "password",
                "Password",
                password,
                setPassword,
                showPassword,
                setShowPassword
              )}

              <CButton
                type="submit"
                color="primary"
                disabled={submitting}
                className="login-submit-btn"
              >
                {submitting ? "Signing in..." : "Sign in"}
              </CButton>

              <p className="login-switch">
                Don&apos;t have an account?{" "}
                <button
                  type="button"
                  className="login-switch-link"
                  onClick={() => switchMode("register")}
                >
                  Create one
                </button>
              </p>
            </CForm>
          )}

          {/* ================= LOGIN: phone code ================= */}
          {mode === "login" && loginStep === "phone" && (
            <CForm onSubmit={handleLoginCode}>
              <CodeField
                id="loginCode"
                value={loginCode}
                onChange={setLoginCode}
              />

              <div className="login-trust">
                <label className="login-trust-row" htmlFor="trustDevice">
                  <input
                    id="trustDevice"
                    type="checkbox"
                    className="login-trust-input"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                  />
                  <span>Trust this device for 30 days</span>
                </label>
                <p className="login-trust-hint">
                  Skip the code on this browser. Only use on your own devices.
                </p>
              </div>

              <CButton
                type="submit"
                color="primary"
                disabled={submitting || loginCode.length !== 6}
                className="login-submit-btn"
              >
                {submitting ? "Verifying..." : "Verify and sign in"}
              </CButton>

              <p className="login-switch">
                Didn&apos;t receive the code?{" "}
                <button
                  type="button"
                  className="login-switch-link"
                  disabled={submitting || resendCooldown > 0}
                  onClick={handleResendLoginCode}
                >
                  {resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : "Resend code"}
                </button>
              </p>

              <p className="login-switch">
                <button
                  type="button"
                  className="login-switch-link"
                  disabled={submitting}
                  onClick={handleBackToLogin}
                >
                  Back to sign in
                </button>
              </p>
            </CForm>
          )}

          {/* ================= REGISTER: details ================= */}
          {mode === "register" && regStep === "details" && (
            <CForm onSubmit={handleRegister}>
              <CFormLabel htmlFor="regUsername" className="login-label">
                Username
              </CFormLabel>
              <CFormInput
                id="regUsername"
                className="mb-3"
                value={regUsername}
                onChange={(e) => setRegUsername(e.target.value)}
                required
              />

              <CFormLabel htmlFor="regEmail" className="login-label">
                Email
              </CFormLabel>
              <CFormInput
                id="regEmail"
                type="email"
                className="mb-3"
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                required
              />

              <CFormLabel htmlFor="regPhone" className="login-label">
                Phone number
              </CFormLabel>
              <CFormInput
                id="regPhone"
                type="tel"
                autoComplete="tel"
                className="mb-1"
                placeholder="+201001234567"
                value={regPhone}
                onChange={(e) => setRegPhone(e.target.value)}
                required
              />
              <p className="login-hint">
                Include the country code. We&apos;ll text you a code to verify
                it and for every sign-in.
              </p>

              {passwordField(
                "regPassword",
                "Password",
                regPassword,
                setRegPassword,
                showRegPassword,
                setShowRegPassword
              )}

              {passwordField(
                "regConfirm",
                "Confirm password",
                regConfirm,
                setRegConfirm,
                showRegConfirm,
                setShowRegConfirm
              )}

              <CButton
                type="submit"
                color="primary"
                disabled={submitting}
                className="login-submit-btn"
              >
                {submitting ? "Sending verification code..." : "Continue"}
              </CButton>

              <p className="login-switch">
                Already have an account?{" "}
                <button
                  type="button"
                  className="login-switch-link"
                  onClick={() => switchMode("login")}
                >
                  Sign in
                </button>
              </p>
            </CForm>
          )}

          {/* ================= REGISTER: email code ================= */}
          {mode === "register" && regStep === "email" && (
            <CForm onSubmit={handleVerifyEmail}>
              <p className="login-sent-to">
                We sent a 6-digit code to <strong>{regEmail}</strong>
              </p>

              <CodeField
                id="emailCode"
                value={emailCode}
                onChange={setEmailCode}
              />

              <CButton
                type="submit"
                color="primary"
                disabled={submitting || emailCode.length !== 6}
                className="login-submit-btn"
              >
                {submitting ? "Verifying..." : "Verify email"}
              </CButton>

              <p className="login-switch">
                Didn&apos;t receive the code?{" "}
                <button
                  type="button"
                  className="login-switch-link"
                  disabled={submitting}
                  onClick={() => handleResend("email")}
                >
                  Resend code
                </button>
              </p>

              <p className="login-switch">
                <button
                  type="button"
                  className="login-switch-link"
                  disabled={submitting}
                  onClick={handleBackToDetails}
                >
                  Change details
                </button>
              </p>
            </CForm>
          )}

          {/* ================= REGISTER: phone code ================= */}
          {mode === "register" && regStep === "phone" && (
            <CForm onSubmit={handleVerifyPhone}>
              <p className="login-sent-to">
                We sent a 6-digit code to <strong>{normalizePhone(regPhone)}</strong>
              </p>

              <CodeField
                id="phoneCode"
                value={phoneCode}
                onChange={setPhoneCode}
              />

              <CButton
                type="submit"
                color="primary"
                disabled={submitting || phoneCode.length !== 6}
                className="login-submit-btn"
              >
                {submitting ? "Verifying..." : "Verify phone"}
              </CButton>

              <p className="login-switch">
                Didn&apos;t receive the code?{" "}
                <button
                  type="button"
                  className="login-switch-link"
                  disabled={submitting}
                  onClick={() => handleResend("phone")}
                >
                  Resend code
                </button>
              </p>
            </CForm>
          )}

          {/* ================= REGISTER: done ================= */}
          {mode === "register" && regStep === "done" && (
            <div className="reg-success">
              <div className="reg-success-icon">
                <Check size={30} strokeWidth={3} />
              </div>
              <p>
                Your email and phone number are verified. An administrator will
                review your account, and you&apos;ll be able to sign in as soon
                as it&apos;s approved.
              </p>
              <CButton
                type="button"
                color="primary"
                className="login-submit-btn"
                onClick={goToSignIn}
              >
                Continue to sign in
              </CButton>
            </div>
          )}
        </div>

        <p className="login-footnote">
          <ShieldCheck size={14} />
          Protected with two-step verification
        </p>
      </div>
    </div>
  )
}

export default LoginPage
